import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Checkbox, FormControlLabel } from "@mui/material";
import apiService from "../components/ReasuableFuncations/api";

const Root = styled("div")(({ theme }) => ({
  width: "100%",
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  "& > :not(style) ~ :not(style)": {
    marginTop: theme.spacing(2),
  },
}));

const countryCodes = [
  { code: "+1", country: "United States" },
  { code: "+1", country: "Canada" },
  { code: "+44", country: "United Kingdom" },
  { code: "+61", country: "Australia" },
  { code: "+49", country: "Germany" },
  { code: "+33", country: "France" },
  { code: "+39", country: "Italy" },
  { code: "+81", country: "Japan" },
  { code: "+86", country: "China" },
  { code: "+91", country: "India" },
  { code: "+34", country: "Spain" },
  { code: "+41", country: "Switzerland" },
  { code: "+46", country: "Sweden" },
  { code: "+31", country: "Netherlands" },
  { code: "+55", country: "Brazil" },
  { code: "+61", country: "New Zealand" },
  { code: "+65", country: "Singapore" },
  { code: "+66", country: "Thailand" },
  { code: "+7", country: "Russia" },
  { code: "+60", country: "Malaysia" },
  { code: "+52", country: "Mexico" },
  { code: "+63", country: "Philippines" },
  { code: "+27", country: "South Africa" },

  // Additional African countries
  { code: "+213", country: "Algeria" },
  { code: "+244", country: "Angola" },
  { code: "+229", country: "Benin" },
  { code: "+267", country: "Botswana" },
  { code: "+226", country: "Burkina Faso" },
  { code: "+257", country: "Burundi" },
  { code: "+237", country: "Cameroon" },
  { code: "+238", country: "Cape Verde" },
  { code: "+236", country: "Central African Republic" },
  { code: "+235", country: "Chad" },
  { code: "+269", country: "Comoros" },
  { code: "+243", country: "Democratic Republic of the Congo" },
  { code: "+242", country: "Republic of the Congo" },
  { code: "+225", country: "Côte d'Ivoire" },
  { code: "+253", country: "Djibouti" },
  { code: "+20", country: "Egypt" },
  { code: "+240", country: "Equatorial Guinea" },
  { code: "+291", country: "Eritrea" },
  { code: "+251", country: "Ethiopia" },
  { code: "+241", country: "Gabon" },
  { code: "+220", country: "Gambia" },
  { code: "+233", country: "Ghana" },
  { code: "+224", country: "Guinea" },
  { code: "+245", country: "Guinea-Bissau" },
  { code: "+254", country: "Kenya" },
  { code: "+266", country: "Lesotho" },
  { code: "+231", country: "Liberia" },
  { code: "+218", country: "Libya" },
  { code: "+261", country: "Madagascar" },
  { code: "+265", country: "Malawi" },
  { code: "+223", country: "Mali" },
  { code: "+222", country: "Mauritania" },
  { code: "+230", country: "Mauritius" },
  { code: "+212", country: "Morocco" },
  { code: "+258", country: "Mozambique" },
  { code: "+264", country: "Namibia" },
  { code: "+227", country: "Niger" },
  { code: "+234", country: "Nigeria" },
  { code: "+250", country: "Rwanda" },
  { code: "+290", country: "Saint Helena" },
  { code: "+248", country: "Seychelles" },
  { code: "+232", country: "Sierra Leone" },
  { code: "+252", country: "Somalia" },
  { code: "+211", country: "South Sudan" },
  { code: "+249", country: "Sudan" },
  { code: "+268", country: "Eswatini" },
  { code: "+255", country: "Tanzania" },
  { code: "+228", country: "Togo" },
  { code: "+216", country: "Tunisia" },
  { code: "+256", country: "Uganda" },
  { code: "+260", country: "Zambia" },
  { code: "+263", country: "Zimbabwe" },
];

const Login = () => {
  const [contactInfo, setContactInfo] = useState("");
  const [countryCode, setCountryCode] = useState("+27");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openLoadingDialog, setOpenLoadingDialog] = useState(false);
  const [loginMethod, setLoginMethod] = useState("email");
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const navigate = useNavigate();

  const handlePolicyAgreementChange = (event) => {
    setPolicyAgreed(event.target.checked);
  };

  const isFormValid = () => {
    return contactInfo && password && policyAgreed && !loading;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    setOpenLoadingDialog(true);

    const loginData =
      loginMethod === "phone" ? `${countryCode}${contactInfo}` : contactInfo;

    try {
      const response = await apiService.post("/api/login", {
        contactInfo: loginData,
        password,
      });
      const data = response.data;

      if (response.statusText === "OK") {
        localStorage.setItem("contactInfo", loginData);
        // Store OTP if provided in development mode
        if (data.otp) {
          localStorage.setItem("autoFillOtp", data.otp);
        }
        setSuccess("Login successful!");
        navigate("/verifycodepopup");
      } else {
        setError(data.message || "Login failed. Please try again.");
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
      setOpenLoadingDialog(false);
    }
  };

  useEffect(() => {
    const tokenExpiration = localStorage.getItem("tokenExpiration");
    const currentTime = new Date().getTime();

    if (tokenExpiration && currentTime > tokenExpiration) {
      localStorage.clear();
      setError("Session expired. Please log in again.");
      navigate("/login");
    }
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        height: "100vh",
        overflow: "hidden",
        backgroundImage: "url(./background.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <AppBar
        position="static"
        sx={{ backgroundColor: "transparent", boxShadow: "none" }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center">
          <img
              src="./l2.png"
              alt="COMESA Logo"
              style={{ maxWidth: "50px", marginRight: "16px" }}
            />
            <Typography variant="h6">Portal</Typography>
          </Box>
          <Button color="inherit" component={Link} to="/about">
            About
          </Button>
        </Toolbar>
      </AppBar>

      <Container
        maxWidth="md"
        sx={{
          paddingRight: "24px",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 4,
          width: "100%",
        }}
      >
        <Paper
          elevation={20}
          sx={{
            p: 4,
            bgcolor: "rgba(255, 255, 255)",
            width: "80%",
            maxWidth: "600px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              sx={{ my: 2, fontWeight: "bold" }}
            >
              Login
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1, width: "100%" }}
            >
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              {/* Login Method Selection */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mb: 2,
                  marginTop: 2,
                }}
              >
                {/* <Button
                  variant={loginMethod === "email" ? "contained" : "outlined"}
                  onClick={() => setLoginMethod("email")}
                >
                  Login with Email
                </Button> */}
                {/* <Button
                  variant={loginMethod === "phone" ? "contained" : "outlined"}
                  onClick={() => setLoginMethod("phone")}
                >
                  Login with Phone
                </Button> */}
              </Box>

              {loginMethod === "email" ? (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="contactInfo"
                  label="Email"
                  name="contactInfo"
                  autoComplete="email"
                  autoFocus
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // Change layout based on screen size
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    sx={{ width: { xs: "100%", sm: "25%" }, height: "25%" }} // Full width on mobile, specific width on larger screens
                  >
                    {countryCodes.map((code) => (
                      <MenuItem key={code.code} value={code.code}>
                        {code.code} ({code.country})
                      </MenuItem>
                    ))}
                  </Select>

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="contactInfo"
                    label="Phone Number"
                    type="number"
                    name="contactInfo"
                    autoComplete="tel"
                    autoFocus
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    sx={{ width: { xs: "100%", sm: "75%" } }} // Full width on mobile, specific width on larger screens
                  />
                </Box>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="agreeToPolicy"
                    checked={policyAgreed}
                    onChange={handlePolicyAgreementChange}
                  />
                }
                label={
                  <Typography sx={{ my: 2, fontSize: "10px" }}>
                    By logging in, you agree to our Privacy Policy & Cookie
                    Policy.
                  </Typography>
                }
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={!isFormValid()}
              >
                Login
              </Button>
              <Root>
                <Divider>OR</Divider>
              </Root>
              <Grid
                container
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Grid
                  item
                  sx={{ display: "flex", justifyContent: "flex-start" }}
                >
                  <Link to="/forgotpassword">Forgot password?</Link>
                </Grid>
                <Grid item sx={{ display: "flex", justifyContent: "flex-end" }}>
                  Don't have an account?_
                  <Link to="/register"> Sign up</Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Dialog
        open={openLoadingDialog}
        onClose={() => setOpenLoadingDialog(false)}
      >
        <DialogTitle>Logging In</DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>

      <Box
        component="footer"
        sx={{
          backgroundColor: "#333",
          color: "#fff",
          padding: "10px",
          textAlign: "center",
        }}
      >
        <Typography variant="body2">
          ©️ {new Date().getFullYear()} EA-SA-IO ICT M&E Portal. All rights
          reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
